import { createRouter, createWebHistory } from "vue-router"

import store from "../store/index"
import logger from "@/services/logger"
import { setTopologyId } from "@/services/logger"
import { useEditorActionPolicy } from "@/composables/useEditorActionPolicy"
import { isReadOnlyModule } from "@/constants/modules"
import {
  hasAuthoritativeAccessProfile,
  moduleAccessLevelSatisfies,
  resolveModuleAccessLevel,
} from "@/security/moduleAccessAuthority"

const DEFAULT_TITLE = "Editor"

// LLM:METADATA
// :topology: [Domain:Identity_Management | Role:Policy_Enforcement | Pattern:RouterGuard]
// :relates-to: uses: "Vuex Store", uses: "Logger Service"
// :rationale: "Router guard protects authenticated routes by checking auth status state machine"
// :contract: pre: "Navigation attempt", post: "Navigation allowed or redirected", invariant: "Protected routes require authenticated status"
// :decision_cache: "Chose auth status check over role check to prevent fan-out before auth validation [ROUTER-002]"
// LLM:END

const TOPOLOGY_ROUTER = "ROUTER"
setTopologyId(TOPOLOGY_ROUTER)

const LEGACY_CAPABILITY_FALLBACKS = {
  "modules.view": "modules",
  "users.view": "users",
  "users.edit": "users",
  "pages.view": "pages",
  "pages.edit": "pages",
  "seo.view": "seo",
  "carts.view": "carts",
  "system.options.view": "options",
}

const hasCapability = (profile, capability) => {
  if (!capability) return false
  if (profile?.is_superadmin) return true
  return profile?.capabilities?.[capability] === true
}

const hasLegacyCapability = (legacyPermissions, capability) => {
  const legacyKey = LEGACY_CAPABILITY_FALLBACKS[capability]
  if (!legacyKey) return false
  return legacyPermissions?.[legacyKey] === true
}

/**
 * Resolve the current editor user id for route-level onlyForUser checks.
 *
 * @precondition store/localStorage may expose user id as number-like string, number, or nullish.
 * @postcondition Returns a finite numeric user id when available from store getter first and localStorage fallback second; otherwise returns null.
 * @mutates none
 */
const resolveCurrentUserId = () => {
  const storeUserId =
    typeof store.getters?.getUserId === "function"
      ? store.getters.getUserId()
      : null
  const normalizedStoreUserId = Number(storeUserId)
  if (Number.isFinite(normalizedStoreUserId) && normalizedStoreUserId > 0) {
    return normalizedStoreUserId
  }

  const storage =
    typeof window !== "undefined" && window.localStorage
      ? window.localStorage
      : null
  if (!storage) return null

  const localStorageUserId = Number(storage.getItem("userId"))
  if (!Number.isFinite(localStorageUserId) || localStorageUserId <= 0)
    return null
  return localStorageUserId
}

/**
 * Show one user-visible denial message for client-side route guards.
 *
 * @precondition store exposes the legacy setNotifications action.
 * @postcondition The user sees why navigation/action was denied instead of a silent redirect.
 * @mutates Vuex notification queue.
 */
const notifyAccessDenied = () => {
  const currentNotifications =
    typeof store.getters?.getNotifications === "function"
      ? store.getters.getNotifications()
      : []
  store.dispatch?.("setNotifications", [
    ...(Array.isArray(currentNotifications) ? currentNotifications : []),
    {
      title: "",
      text: "Недостаточно прав для этого действия.",
      type: "error",
      pinned: false,
    },
  ])
}

/**
 * Returns whether the current route guard should allow reports-shell access.
 *
 * @precondition profile is either nullish or a backend-derived access profile object; legacyPermissions is the current compatibility ACL for userRole.
 * @postcondition Prefers authoritative backend report grants and only falls back to legacy role permissions when no authoritative profile is present.
 * @mutates none
 */
const hasReportRouteAccess = (profile, legacyPermissions = {}) => {
  if (!hasAuthoritativeAccessProfile(profile)) {
    if (legacyPermissions?.reports === true) return true
    if (Array.isArray(legacyPermissions?.reports)) {
      return legacyPermissions.reports.some(
        (reportName) => typeof reportName === "string" && reportName !== ""
      )
    }
    return false
  }

  if (profile.is_superadmin) return true
  if (Array.isArray(profile.reports)) {
    return profile.reports.some(
      (reportName) => typeof reportName === "string" && reportName !== ""
    )
  }
  if (profile?.is_superadmin) return true
  if (!Array.isArray(profile?.reports)) return false
  return profile.reports.some(
    (reportName) => typeof reportName === "string" && reportName !== ""
  )
}

/**
 * Returns whether the current route guard should allow one named report surface.
 *
 * @precondition reportName is the backend-owned canonical named-report key for the route.
 * @postcondition Allows superadmin, exact backend report grant, or legacy broad reports access only when no authoritative profile exists.
 * @mutates none
 */
const hasNamedReportRouteAccess = (
  profile,
  legacyPermissions = {},
  reportName
) => {
  if (!reportName) return false

  if (!hasAuthoritativeAccessProfile(profile)) {
    if (legacyPermissions?.reports === true) return true
    if (!Array.isArray(legacyPermissions?.reports)) return false
    return legacyPermissions.reports.includes(reportName)
  }

  if (profile?.is_superadmin) return true
  if (!Array.isArray(profile?.reports)) return false
  return profile.reports.includes(reportName)
}

const routes = [
  {
    path: "/",
    name: "home",
    component: () =>
      import(/* webpackChunkName: "about" */ "../views/Dashboard.vue"),
    meta: { transition: "pages-fade", title: DEFAULT_TITLE + " – Dashboard" },
  },
  {
    path: "/modules/:id",
    name: "module",
    meta: {
      transition: "pages-fade",
      title: DEFAULT_TITLE + " – Модули",
      requiresModuleAccess: { param: "id", level: "view" },
      actionPolicy: "module.item.view",
    },
    component: () =>
      import(/* webpackChunkName: "about" */ "../views/ModuleItems.vue"),
  },
  {
    path: "/modules",
    name: "module_list",
    meta: {
      transition: "pages-fade",
      title: DEFAULT_TITLE + " – Модули",
      actionPolicy: "module.list.view",
    },
    component: () =>
      import(/* webpackChunkName: "about" */ "../views/Modules.vue"),
  },
  {
    path: "/users",
    name: "users",
    meta: {
      transition: "pages-fade",
      title: DEFAULT_TITLE + " – Пользователи",
      requiresAuth: true,
      requiresCapability: "users.view",
      actionPolicy: "users.view",
    },
    component: () =>
      import(/* webpackChunkName: "about" */ "../views/Users.vue"),
  },
  {
    path: "/users/add",
    name: "users_add",
    meta: { transition: "pages-fade", requiresCapability: "users.edit" },
    component: () =>
      import(/* webpackChunkName: "about" */ "../views/UsersAdd.vue"),
  },
  {
    path: "/users/:userid",
    name: "users_edit",
    meta: { transition: "pages-fade", requiresCapability: "users.edit" },
    component: () =>
      import(/* webpackChunkName: "about" */ "../views/UsersAdd.vue"),
  },
  {
    path: "/pages",
    name: "pages",
    meta: {
      transition: "pages-fade",
      requiresCapability: "pages.view",
      actionPolicy: "pages.view",
    },
    component: () =>
      import(/* webpackChunkName: "about" */ "../views/PagesList.vue"),
  },
  {
    path: "/pages/:pageid",
    name: "pages_edit",
    meta: { transition: "pages-fade", requiresCapability: "pages.edit" },
    component: () =>
      import(/* webpackChunkName: "about" */ "../views/PagesEdit.vue"),
  },
  {
    path: "/reports",
    name: "reports",
    meta: {
      transition: "pages-fade",
      title: DEFAULT_TITLE + " – Отчеты",
      requiresReportsAccess: true,
      actionPolicy: "reports.overview.view",
    },
    component: () =>
      import(/* webpackChunkName: "about" */ "../views/ReportsView.vue"),
  },
  {
    path: "/modules/:id/add",
    name: "module_add_item",
    meta: {
      transition: "pages-fade",
      requiresModuleAccess: { param: "id", level: "edit" },
      actionPolicy: "module.item.create",
    },
    component: () => import("../views/ModuleNewItem.vue"),
    children: [
      {
        path: "clone/:cloneid",
        name: "module_clone_item",
        meta: {
          transition: "pages-fade",
          requiresModuleAccess: { param: "id", level: "edit" },
          actionPolicy: "module.item.create",
        },
        component: () => import("../views/ModuleNewItem.vue"),
      },
    ],
  },

  {
    path: "/modules/:id/multiedit",
    name: "module_multi_edit",
    meta: {
      transition: "pages-fade",
      requiresModuleAccess: { param: "id", level: "edit" },
      actionPolicy: "module.item.save",
    },
    component: () => import("../views/ModuleItemsMultiedit.vue"),
  },

  {
    path: "/modules/:id/edit/:itemid",
    name: "module_edit_item",
    meta: {
      transition: "pages-fade",
      requiresModuleAccess: { param: "id", level: "edit" },
      actionPolicy: "module.item.save",
    },
    component: () =>
      import(/* webpackChunkName: "about" */ "../views/ModuleNewItem.vue"),
  },
  {
    path: "/services/sales_1c",
    name: "sales_1c",
    meta: { transition: "pages-fade", requiresCapability: "services.sales_1c" },
    component: () =>
      import(/* webpackChunkName: "about" */ "../views/ServicesSales.vue"),
  },
  {
    path: "/services/compare_1c",
    name: "compare_1c",
    meta: {
      transition: "pages-fade",
      requiresCapability: "services.compare_1c",
      actionPolicy: "services.compare_1c.run",
    },
    component: () =>
      import(/* webpackChunkName: "about" */ "../views/ServicesCompare.vue"),
  },
  {
    path: "/services/prices",
    name: "prices",
    meta: { transition: "pages-fade", requiresCapability: "services.prices" },
    component: () =>
      import(/* webpackChunkName: "about" */ "../views/ServicesPrices.vue"),
  },
  {
    path: "/services/refresh",
    name: "refresh",
    meta: { transition: "pages-fade", requiresCapability: "services.refresh" },
    component: () => import("../views/ServicesRefresh.vue"),
  },
  {
    path: "/services/revaluation",
    name: "revaluation",
    meta: {
      transition: "pages-fade",
      requiresCapability: "services.revaluation",
    },
    component: () =>
      import(
        /* webpackChunkName: "about" */ "../views/ServicesRevaluation.vue"
      ),
  },
  {
    path: "/services/tag_sync",
    name: "tag_sync",
    meta: {
      transition: "pages-fade",
      requiresCapability: "services.tag_sync",
    },
    component: () => import("../views/ServicesTagSync.vue"),
  },
  {
    path: "/services/emails",
    name: "emails",
    meta: {
      transition: "pages-fade",
      requiresReport: "special",
      requiresCapability: "services.emails",
    },
    component: () =>
      import(/* webpackChunkName: "about" */ "../views/ServicesEmails.vue"),
  },
  {
    path: "/services/artist_change",
    name: "artist_change",
    meta: {
      transition: "pages-fade",
      requiresCapability: "services.artist_change",
    },
    component: () => import("../views/ServicesArtistUpdate.vue"),
  },
  {
    path: "/services/artist-update",
    name: "artist_update_legacy",
    meta: {
      transition: "pages-fade",
      requiresCapability: "services.artist_change",
    },
    component: () => import("../views/ServicesArtistUpdate.vue"),
  },
  {
    path: "/services/market_price",
    name: "market_price",
    meta: {
      transition: "pages-fade",
      requiresCapability: "services.market_price",
    },
    component: () => import("../views/ServicesMarketPrice.vue"),
  },
  {
    path: "/options",
    name: "options",
    meta: {
      transition: "pages-fade",
      title: DEFAULT_TITLE + " – Параметры",
      requiresCapability: "system.options.view",
      actionPolicy: "system.options.view",
    },
    component: () =>
      import(/* webpackChunkName: "about" */ "../views/Options.vue"),
  },
  {
    path: "/access",
    name: "access",
    meta: {
      transition: "pages-fade",
      title: DEFAULT_TITLE + " – Доступ",
      requiresCapability: "roles.manage",
      actionPolicy: "access.roles.manage",
    },
    component: () => import("../views/AccessView.vue"),
  },
  {
    path: "/access/roles",
    name: "access_roles",
    meta: {
      transition: "pages-fade",
      title: DEFAULT_TITLE + " – Роли доступа",
      requiresCapability: "roles.manage",
    },
    component: () => import("../views/AccessRoles.vue"),
  },
  {
    path: "/access/roles/:id",
    name: "access_role_edit",
    meta: {
      transition: "pages-fade",
      title: DEFAULT_TITLE + " – Редактирование роли",
      requiresCapability: "roles.manage",
    },
    component: () => import("../views/AccessRoleEdit.vue"),
  },
  {
    path: "/access/admins",
    name: "access_admins",
    meta: {
      transition: "pages-fade",
      title: DEFAULT_TITLE + " – Назначение ролей",
      requiresCapability: "roles.manage",
    },
    component: () => import("../views/AccessAdmins.vue"),
  },
  {
    path: "/seo",
    name: "seo",
    meta: {
      transition: "pages-fade",
      title: DEFAULT_TITLE + " – SEO",
      requiresCapability: "seo.view",
      actionPolicy: "seo.view",
    },
    component: () => import(/* webpackChunkName: "about" */ "../views/Seo.vue"),
  },
  {
    path: "/carts",
    name: "carts",
    meta: {
      transition: "pages-fade",
      title: DEFAULT_TITLE + " – Корзины",
      requiresCapability: "carts.view",
      actionPolicy: "carts.view",
    },
    component: () =>
      import(/* webpackChunkName: "about" */ "../views/Carts.vue"),
  },
  {
    path: "/login",
    name: "login",
    meta: { transition: "pages-fade", title: DEFAULT_TITLE + " – вход" },
    component: () =>
      import(/* webpackChunkName: "about" */ "../views/Login.vue"),
  },
  // {
  //   path: "/artists/:id",
  //   name: "artist",
  //   meta: { transition: "none" },
  //   component: () =>
  //     import(/* webpackChunkName: "about" */ "../views/ArtistView.vue"),
  // },
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  // scrollBehavior: (to, from, savedPosition) => {
  //   if (savedPosition) {
  //     return savedPosition
  //   } else if (to.hash) {
  //     return {
  //       selector: to.hash,
  //     t
  //   } else {
  //     return { x: 0, y: 0 }
  //   }
  // },
})

// LLM:METADATA
// :topology: [Domain:Identity_Management | Role:NavigationController | Pattern:StateWatcher]
// :relates-to: uses: "Vuex Store", uses: "Vue Router"
// :rationale: "Watches auth status changes to retry navigation when auth becomes ready"
// :contract: pre: "Auth status changes from pending to final", post: "Navigation is retried", invariant: "Only retries once per status change"
// :decision_cache: "Chose watcher-based retry for async auth resolution [ROUTER-WATCH-001]"
// LLM:END
let pendingNavigation = null
let unwatch = null

// Watch for auth status changes and retry pending navigation
const setupAuthWatcher = () => {
  if (unwatch) return // Already set up

  unwatch = store.watch(
    (state) => state.auth.authStatus,
    (newStatus, oldStatus) => {
      // Only act if we were in a pending state and now have a final state
      const wasPending = oldStatus === "unknown" || oldStatus === "checking"
      const isFinal =
        newStatus === "authenticated" || newStatus === "unauthenticated"

      if (wasPending && isFinal && pendingNavigation) {
        logger.info(TOPOLOGY_ROUTER, "ROUTER_RETRY_AFTER_AUTH_READY", {
          oldStatus,
          newStatus,
          pendingRoute: pendingNavigation.to.name,
        })
        // Retry the navigation
        router.push(pendingNavigation.to).catch((err) => {
          // Navigation might be duplicated or aborted, which is fine
          if (err.name !== "NavigationDuplicated") {
            logger.warn(TOPOLOGY_ROUTER, "ROUTER_RETRY_FAILED", {
              error: err.message,
            })
          }
        })
        pendingNavigation = null
      }
    }
  )
}

router.beforeEach(function (to, from, next) {
  const authStatus = store.getters.getAuthStatus()
  const userRole = store.getters.getUserRole()
  const accessProfile = store.getters.getAccessProfile()
  const legacyPermissions = store.getters.getUserRolePermissions
    ? store.getters.getUserRolePermissions()
    : {}
  let availableModules = store.getters.getModules
    ? store.getters.getModules()
    : []
  const requiredCapability = to.meta?.requiresCapability || null
  const requiredUsers = Array.isArray(to.meta?.onlyForUser)
    ? to.meta.onlyForUser
    : null
  const requiredModuleAccess = to.meta?.requiresModuleAccess || null
  const requiresReportsAccess = to.meta?.requiresReportsAccess === true
  const requiredReport = to.meta?.requiresReport || null
  const requiredActionPolicy = to.meta?.actionPolicy || null
  const isLoginRoute = to.name === "login"
  const isUnauthenticated = !userRole || userRole === ""

  logger.debug(TOPOLOGY_ROUTER, "ROUTER_GUARD_CHECK", {
    to: to.name,
    from: from.name,
    authStatus,
    userRole,
    isLoginRoute,
    isUnauthenticated,
    requiredCapability,
    requiredModuleAccess,
    requiresReportsAccess,
    requiredReport,
    requiredActionPolicy,
  })

  // LLM:METADATA
  // :topology: [Domain:Identity_Management | Role:NavigationGuard | Pattern:StateGate]
  // :relates-to: uses: "Vuex Store", uses: "Logger Service"
  // :rationale: "Blocks navigation to protected routes until auth status is resolved"
  // :contract: pre: "Navigation attempted", post: "Navigation allowed or redirected", invariant: "Protected routes wait for authenticated status"
  // :decision_cache: "Chose status check to prevent race condition with auth bootstrap [ROUTER-GUARD-001]"
  // LLM:END

  if (authStatus === "unknown" || authStatus === "checking") {
    logger.warn(TOPOLOGY_ROUTER, "ROUTER_BLOCK_AUTH_PENDING", {
      to: to.name,
      reason: "Auth status not yet resolved",
      authStatus,
    })
    pendingNavigation = { to, from }
    setupAuthWatcher()
    return
  }

  if (authStatus === "unauthenticated" && !isLoginRoute) {
    logger.warn(TOPOLOGY_ROUTER, "ROUTER_REDIRECT_TO_LOGIN", {
      to: to.name,
      reason: "User not authenticated",
      authStatus,
    })
    next({ name: "login" })
    return
  }

  if (
    requiredCapability &&
    !(
      hasCapability(accessProfile, requiredCapability) ||
      (!hasAuthoritativeAccessProfile(accessProfile) &&
        hasLegacyCapability(legacyPermissions, requiredCapability))
    )
  ) {
    logger.warn(TOPOLOGY_ROUTER, "ROUTER_REDIRECT_FORBIDDEN_CAPABILITY", {
      to: to.name,
      requiredCapability,
    })
    notifyAccessDenied()
    next({ name: "home" })
    return
  }

  if (requiredUsers) {
    const currentUserId = resolveCurrentUserId()
    const hasOnlyForUserAccess =
      Number.isFinite(currentUserId) && requiredUsers.includes(currentUserId)

    if (!hasOnlyForUserAccess) {
      logger.warn(TOPOLOGY_ROUTER, "ROUTER_REDIRECT_FORBIDDEN_ONLY_FOR_USER", {
        to: to.name,
        requiredUsers,
        currentUserId,
      })
      notifyAccessDenied()
      next({ name: "home" })
      return
    }
  }

  if (
    requiresReportsAccess &&
    !hasReportRouteAccess(accessProfile, legacyPermissions)
  ) {
    logger.warn(TOPOLOGY_ROUTER, "ROUTER_REDIRECT_FORBIDDEN_REPORTS", {
      to: to.name,
    })
    notifyAccessDenied()
    next({ name: "home" })
    return
  }

  if (
    requiredReport &&
    !hasNamedReportRouteAccess(accessProfile, legacyPermissions, requiredReport)
  ) {
    logger.warn(TOPOLOGY_ROUTER, "ROUTER_REDIRECT_FORBIDDEN_NAMED_REPORT", {
      to: to.name,
      requiredReport,
    })
    notifyAccessDenied()
    next({ name: "home" })
    return
  }

  const routeModuleId = Number(to.params?.id || 0)
  const readOnlyMutationRoutes = new Set([
    "module_add_item",
    "module_clone_item",
    "module_multi_edit",
    "module_edit_item",
  ])
  if (
    readOnlyMutationRoutes.has(to.name) &&
    Number.isFinite(routeModuleId) &&
    isReadOnlyModule(routeModuleId)
  ) {
    const redirect = { name: "module", params: { id: String(routeModuleId) } }
    const itemId = to.params?.itemid || to.params?.cloneid
    if (itemId) redirect.query = { search: String(itemId) }
    next(redirect)
    return
  }

  const evaluateModulePolicy = () => {
    if (
      requiredActionPolicy &&
      (hasAuthoritativeAccessProfile(accessProfile) ||
        requiredActionPolicy === "module.list.view")
    ) {
      const moduleScopeParam = requiredModuleAccess?.param || "id"
      const routeModuleIdRaw = to.params?.[moduleScopeParam]
      const routeModuleId = Number(routeModuleIdRaw)
      const policyModuleId =
        Number.isFinite(routeModuleId) && routeModuleId > 0
          ? routeModuleId
          : null
      const editorActionPolicy = useEditorActionPolicy({
        accessProfile,
        legacyPermissions,
        modules: availableModules,
        moduleId: policyModuleId,
      })

      if (!editorActionPolicy.can(requiredActionPolicy)) {
        logger.warn(
          TOPOLOGY_ROUTER,
          "ROUTER_REDIRECT_FORBIDDEN_ACTION_POLICY",
          {
            to: to.name,
            requiredActionPolicy,
          }
        )
        notifyAccessDenied()
        next({ name: "home" })
        return
      }
    }

    if (requiredModuleAccess) {
      const routeParam = requiredModuleAccess.param || "id"
      const requestedModuleId = Number(to.params?.[routeParam] || 0)
      const requiredLevel = requiredModuleAccess.level || "view"
      const grantedLevel = resolveModuleAccessLevel(
        accessProfile,
        requestedModuleId,
        availableModules,
        legacyPermissions
      )
      const hasModuleAccess = moduleAccessLevelSatisfies(
        grantedLevel,
        requiredLevel
      )

      if (!hasModuleAccess) {
        logger.warn(TOPOLOGY_ROUTER, "ROUTER_REDIRECT_FORBIDDEN_MODULE_SCOPE", {
          to: to.name,
          requestedModuleId,
          requiredLevel,
          grantedLevel,
        })
        notifyAccessDenied()
        next({ name: "home" })
        return
      }
    }

    logger.info(TOPOLOGY_ROUTER, "ROUTER_NAVIGATION_ALLOWED", {
      to: to.name,
      userRole,
      authStatus,
    })
    next()
  }

  if (
    requiredModuleAccess &&
    Array.isArray(availableModules) &&
    availableModules.length === 0
  ) {
    let inventoryLoad
    try {
      inventoryLoad = store.dispatch("ensureModulesList")
    } catch (error) {
      logger.warn(TOPOLOGY_ROUTER, "ROUTER_MODULE_INVENTORY_LOAD_FAILED", {
        to: to.name,
        error: error?.message,
      })
      next({ name: "home" })
      return
    }

    if (inventoryLoad && typeof inventoryLoad.then === "function") {
      return inventoryLoad
        .then(() => {
          availableModules = store.getters.getModules
            ? store.getters.getModules()
            : []
          evaluateModulePolicy()
        })
        .catch((error) => {
          logger.warn(TOPOLOGY_ROUTER, "ROUTER_MODULE_INVENTORY_LOAD_FAILED", {
            to: to.name,
            error: error?.message,
          })
          next({ name: "home" })
        })
    }

    availableModules = store.getters.getModules
      ? store.getters.getModules()
      : []
  }

  evaluateModulePolicy()

  // if (to.meta.transition != "none")
  //   // console.log(to.meta.transition != "none")
  //   store.dispatch("setPagesTransition", true)

  // setTimeout(() => {
  //   window.scrollTo(0, 0)
  // }, 100)

  // setTimeout(() => {
  //   store.dispatch("setPagesTransition", false)
  // }, 352.5)

  // next()
})

router.afterEach((to) => {
  // Vue.nextTick(() => {
  document.title = to.meta.title || DEFAULT_TITLE
  // })
})

export default router
